import React, { useEffect, useMemo, useState } from 'react'
import { PhotoIcon } from '@heroicons/react/24/outline'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useIntuneQuery, intuneKeys } from '../hooks/useIntuneQuery'
import { Field, Select, Stepper, inputCls, previewGrid, useStepper } from './ui'
import AssignmentBlade, { AssignmentRow, toAssignmentGroup } from './AssignmentBlade'
import { rowsToAssignments, assignmentsToRows } from '../utils/assignmentPayload'
import { DevNote } from './DevMode'
import { PsadtDialogSurface, PSADT_PREVIEW_MAX_WIDTH } from './PsadtDialogPreview'
import type { PsadtPreviewConfig } from './PsadtDialogPreview'
import {
  ACCENT_PRESETS,
  DEFAULT_FLUENT_ACCENT,
  PSADT_LANGS,
  normalizeAccent,
} from '../utils/uxTemplateDraft'
import {
  BrandingTemplate,
  fetchBrandingImage,
  createBrandingTemplate,
  listAssignmentFilters,
  listAzureADGroups,
  removeBrandingImage,
  setBrandingTemplateAssignments,
  updateBrandingTemplate,
  uploadBrandingImage,
} from '../services/api'
import { errorText } from '../utils/errorText'

/**
 * Author a branding — the appearance a whole DEVICE takes on, not one deployment's.
 *
 * A PAGE, on the same step rail as the user-experience wizard, because it asks the same kind of
 * question at the same length. A modal would put a live dialog preview inside a dialog.
 *
 * The Appearance step is the user-experience wizard's, with one difference that is the whole point:
 * there, images are a note saying they come from a branding; here they are the control. The style
 * decides which asset PSADT draws — Fluent renders Assets.Logo, Classic renders Assets.Banner — so
 * only the one the chosen style uses is offered, and the preview beside it shows what that produces.
 *
 * Every value is written to %ProgramData%\sap\psadt\config on the devices this is deployed to, and
 * the on-device configuration wins over whatever a deployment chose. A value left unset is omitted
 * from that configuration and falls through to each deployment's own choice, which is why every
 * field can be left alone rather than defaulted.
 */

/** The names and sizes PSADT itself ships, quoted to the author because they are what the package
 *  writes and what the renderer measures against — not house style we could soften. */
const ASSET_SPEC = {
  logo: { file: 'Assets\\AppIcon.png', hint: 'PNG. Square reads best — PSADT draws its own at 256×256.' },
  banner: {
    file: 'Assets\\Banner.Classic.png',
    // Not a preference: above this the Fluent renderer yields a null AppBannerImage, so the
    // runtime keeps the bundled banner and the branding is never drawn. Upload refuses it.
    hint: 'PNG, at most 450×100. Larger is rejected — devices would show the default banner.',
  },
} as const

const STEPS = [
  { key: 'basics', label: 'Basics' },
  { key: 'appearance', label: 'Appearance' },
  { key: 'assignment', label: 'Assignment' },
]

interface Draft {
  name: string
  description: string
  companyName: string
  /** PSADT's spelling, as the column stores it. '' leaves the style to each deployment. */
  dialogStyle: '' | 'Fluent' | 'Classic'
  /** '' leaves the accent to the deployment. */
  accent: string
  /** A PSADT language code, or '' to follow each deployment's own setting. */
  language: string
  /** The welcome dialog's wording. '' ships no string table at all, leaving PSADT's own. */
  welcomeText: string
  /** Fluent only: the app's own packaged icon, or an image supplied here. */
  logoSource: 'appIcon' | 'custom'
  /** Chosen but not yet uploaded — a create has no row to attach to until it is saved. */
  pendingLogo: File | null
  pendingBanner: File | null
  /** An existing image the author cleared. Applied on save, so Cancel really cancels. */
  dropLogo: boolean
  dropBanner: boolean
  /** Who this branding goes to. STORED on save — Deploy on the branding row is what publishes it.
   *  Required or Uninstall only: nobody browses to a re-skin in Company Portal. */
  assignments: AssignmentRow[]
}

const draftFrom = (t?: BrandingTemplate): Draft => ({
  name: t?.name ?? '',
  description: t?.description ?? '',
  companyName: t?.company_name ?? '',
  dialogStyle: t?.dialog_style === 'Fluent' || t?.dialog_style === 'Classic' ? t.dialog_style : '',
  accent: t?.accent_color ? normalizeAccent(t.accent_color) : '',
  language: t?.language_override ?? '',
  welcomeText: t?.welcome_custom_text ?? '',
  logoSource: t?.logo_asset_id ? 'custom' : 'appIcon',
  pendingLogo: null,
  pendingBanner: null,
  dropLogo: false,
  dropBanner: false,
  // Seeded from the BRANDING's own audience, which is what it currently says. Deliberately not
  // from its deployments: those carry a frozen copy of what they shipped, and showing those rows
  // here would invite editing a record of the past.
  assignments: assignmentsToRows((t?.assignments as any) ?? []),
})

/** What the author should know about assigning a branding in this state, or null.
 *
 *  A NOTE, NOT A GATE. An audience is stored on the branding by its own endpoint, which accepts one
 *  whatever the build is doing — the audience is not among the bytes the signature covers. Only
 *  DEPLOY needs a signed package, and that is pressed on the branding list. Withholding the blade
 *  until the build finished meant a new branding had to be created, waited on, and reopened before
 *  anyone could say who it was for. */
const assignmentNote = (template?: BrandingTemplate): string | null => {
  if (!template) {
    return 'Choose the audience now and it is saved with the branding. Building and signing happen '
      + 'after that, and Deploy on the branding list sends it to these groups once they finish.'
  }
  if (template.state === 'building') {
    return 'This branding is still being built and signed. The audience saves now; Deploy becomes '
      + 'available on the branding list as soon as the build finishes.'
  }
  if (template.state === 'failed') {
    return 'This branding failed to build, so it cannot be deployed yet — the audience still '
      + 'saves. Build it again from the branding list.'
  }
  return null
}

/**
 * Why this branding cannot be saved yet, or null.
 *
 * The build refuses a branding that specifies nothing: its package would install on every targeted
 * device, satisfy its detection rule and change nothing, with no later step able to catch it. The
 * refusal is caught here so the author is told before the row sits in Failed.
 */
const problem = (d: Draft, hasImage: boolean): string | null => {
  if (!d.name.trim()) return 'Give the branding a name.'
  // An assignment set that only EXCLUDES resolves to nobody: Intune has nothing to exclude from,
  // so the application is created, assigned, and reaches no device while reporting success.
  if (d.assignments.length && !d.assignments.some((a) => (a.mode ?? 'include') !== 'exclude')) {
    return 'This assignment only excludes groups, so it reaches no device. Add a group to send ' +
      'the branding to.'
  }
  if (!d.companyName.trim() && !d.dialogStyle && !d.accent && !d.language &&
      !d.welcomeText.trim() && !hasImage) {
    return 'Set at least one of image, company name, dialog style, accent colour, language or ' +
      'dialog wording. A branding that overrides nothing installs on the device and changes nothing.'
  }
  return null
}

/**
 * The saved image is there, and this editor could not read it back.
 *
 * SAYING SO IS THE WHOLE POINT. Without it a failed load and an empty slot look identical — the
 * preview falls back to the packaged default either way — so an author sees no image, concludes the
 * upload never saved, and uploads it again. Which of the two it is decides what they should do, and
 * only one of them is their problem.
 */
const SavedImageUnreadable: React.FC<{ slot: 'logo' | 'banner' }> = ({ slot }) => (
  <p className="mt-1.5 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2 text-xs text-amber-500">
    This branding has a {slot}, but it could not be loaded to show here — so the preview below is
    showing the packaged default instead. The stored {slot} is unchanged and will still ship. Reload
    the page; if it keeps happening, the browser console names the request that failed.
  </p>
)

/** A file chooser shaped like the mock's: the chosen name in place, or the invitation. */
const FileSlot: React.FC<{
  value: string | null
  onPick: (f: File) => void
  onClear?: () => void
}> = ({ value, onPick, onClear }) => (
  <div className="flex items-center gap-2">
    <label className="flex flex-1 cursor-pointer items-center gap-2 rounded-lg border border-dashed border-theme-border-primary bg-theme-primary px-3 py-2.5 text-sm text-theme-text-tertiary hover:text-theme-text-primary">
      <PhotoIcon className="h-4 w-4 shrink-0" />
      <span className="truncate">{value ?? 'Choose a PNG…'}</span>
      <input
        type="file"
        accept="image/png"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) onPick(f)
          // Cleared so picking the SAME file again still fires a change event.
          e.currentTarget.value = ''
        }}
      />
    </label>
    {value && onClear && (
      <button
        type="button"
        onClick={onClear}
        className="shrink-0 text-[12px] text-theme-text-tertiary hover:text-red-400"
      >
        Remove
      </button>
    )}
  </div>
)

/**
 * A chosen file as something an `<img>` can draw.
 *
 * The URL is created INSIDE the effect that revokes it, so what is on screen is always a URL that
 * effect still owns. Created during render, a cleanup firing without a re-render — which is exactly
 * what React does on mount under StrictMode — revokes the URL the image is pointing at.
 */
function useObjectUrl(file: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!file) {
      setUrl(null)
      return
    }
    const made = URL.createObjectURL(file)
    setUrl(made)
    return () => URL.revokeObjectURL(made)
  }, [file])
  return url
}

/**
 * A branding's STORED image, fetched through the API client.
 *
 * Not `<img src="/api/branding/templates/…/image">`: that route authenticates with a bearer token,
 * and a browser image request carries no Authorization header, so it answered 401 and the editor
 * drew an empty slot — the author sees no banner and concludes the upload was lost.
 *
 * Re-fetched on the asset id, so a re-upload is visible at once rather than served from cache.
 */
function useSavedBrandingImage(
  templateId: string | undefined,
  slot: 'logo' | 'banner',
  assetId: string | null,
): { url: string | null; failed: boolean } {
  const [url, setUrl] = useState<string | null>(null)
  // A LOAD THAT FAILED IS NOT THE SAME AS NO IMAGE, and this used to report both as null. The
  // preview then drew the packaged default — exactly what a branding with no image looks like — so
  // an image that was stored, valid, and merely unreachable was indistinguishable from one that had
  // never been uploaded. That is a stored banner reported as missing, and it is the reason this bug
  // took a server log to find rather than being visible in the editor.
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    if (!templateId || !assetId) {
      setUrl(null)
      setFailed(false)
      return
    }
    let live = true
    let made: string | null = null
    setFailed(false)
    fetchBrandingImage(templateId, slot)
      .then((blob) => {
        if (!live) return
        made = URL.createObjectURL(blob)
        setUrl(made)
      })
      .catch((e) => {
        if (!live) return
        setUrl(null)
        setFailed(true)
        // Named in the console too: the message above tells the author their image is still there,
        // and this says which request failed and how, which is what a bug report needs.
        // eslint-disable-next-line no-console
        console.warn(`[branding] could not load the saved ${slot}`, e?.response?.status ?? e)
      })
    return () => {
      live = false
      if (made) URL.revokeObjectURL(made)
    }
  }, [templateId, slot, assetId])
  return { url, failed }
}

const BrandingWizard: React.FC<{
  /** Absent for a new branding. */
  template?: BrandingTemplate
  onClose: () => void
  onSaved?: () => void
}> = ({ template, onClose, onSaved }) => {
  const qc = useQueryClient()
  const [d, setD] = useState<Draft>(() => draftFrom(template))
  const [stepIdx, setStepIdx] = useState(0)
  const [error, setError] = useState<string | null>(null)
  // Cleared as soon as the name is edited: a field still marked wrong while the author types the
  // fix reads as though the new name were rejected too.
  const [nameTaken, setNameTaken] = useState(false)
  const rail = useStepper(stepIdx)
  const step = STEPS[stepIdx]
  const isLast = stepIdx === STEPS.length - 1

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((prev) => ({ ...prev, [k]: v }))

  // The assignment step targets groups that already exist, so the tenant's Entra groups and its
  // assignment filters are read here — a blade rendered without them offers All users and All
  // devices and nothing else, which is not a group picker.
  const groupsQ = useIntuneQuery({
    queryKey: intuneKeys.adGroups(),
    queryFn: () => listAzureADGroups(),
    retry: false,
  })
  const groups = useMemo(
    () => (groupsQ.data?.groups ?? []).map(toAssignmentGroup),
    [groupsQ.data],
  )
  const filtersQ = useIntuneQuery({
    queryKey: intuneKeys.assignmentFilters(),
    queryFn: listAssignmentFilters,
    retry: false,
  })

  const savedLogo = template?.logo_asset_id && !d.dropLogo ? template.logo_asset_id : null
  const savedBanner = template?.banner_asset_id && !d.dropBanner ? template.banner_asset_id : null

  // A chosen file is previewed before it is uploaded, or the author picks an image and goes on
  // seeing the old one; a stored one is fetched, because the route wants a bearer token.
  const pendingLogoUrl = useObjectUrl(d.pendingLogo)
  const pendingBannerUrl = useObjectUrl(d.pendingBanner)
  const savedLogoImage = useSavedBrandingImage(template?.id, 'logo', savedLogo)
  const savedBannerImage = useSavedBrandingImage(template?.id, 'banner', savedBanner)
  const logoUrl = pendingLogoUrl ?? savedLogoImage.url
  const bannerUrl = pendingBannerUrl ?? savedBannerImage.url
  // Only while the stored one is the one being shown: a newly chosen file replaces it, and a
  // failure to load what it replaces no longer matters.
  const logoLoadFailed = !pendingLogoUrl && savedLogoImage.failed
  const bannerLoadFailed = !pendingBannerUrl && savedBannerImage.failed

  // WHAT THE BRANDING HAS, which is not the same as what the preview can draw yet: a stored image
  // arrives a moment after the step opens. Asked of the draft, so a branding whose only setting is
  // an image is never refused for setting nothing while its bytes are still in flight.
  const hasLogoAsset = d.logoSource === 'custom' && (!!d.pendingLogo || !!savedLogo)
  const hasBannerAsset = !!d.pendingBanner || !!savedBanner
  const hasImage = hasLogoAsset || hasBannerAsset
  // What the preview can draw.
  const hasLogo = d.logoSource === 'custom' && !!logoUrl

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: d.name.trim(),
        description: d.description.trim() || undefined,
        // null, not '': an unset value must clear the column so the configuration omits the key and
        // the deployment's own choice applies.
        company_name: d.companyName.trim() || null,
        dialog_style: d.dialogStyle || null,
        accent_color: d.accent || null,
        language_override: d.language || null,
        welcome_custom_text: d.welcomeText.trim() || null,
      }
      // SAVING AN UNCHANGED BRANDING MUST NOT TOUCH IT. Every PUT returns the template to
      // unsigned — the stored signature covers the bytes that were built — so a save that wrote
      // regardless would un-sign a Ready branding just because someone opened the wizard to assign
      // it, and the deploy below (which needs a signed template) would then never happen. Opening
      // the editor and pressing save would also silently cost a rebuild.
      const touched =
        !template ||
        d.pendingLogo !== null ||
        d.pendingBanner !== null ||
        d.dropLogo ||
        d.dropBanner ||
        body.name !== template.name ||
        (body.description ?? null) !== (template.description ?? null) ||
        body.company_name !== (template.company_name ?? null) ||
        body.dialog_style !== (template.dialog_style ?? null) ||
        body.accent_color !== (template.accent_color ?? null) ||
        body.language_override !== (template.language_override ?? null) ||
        body.welcome_custom_text !== (template.welcome_custom_text ?? null)

      const row = !template
        ? await createBrandingTemplate(body)
        : touched
          ? await updateBrandingTemplate(template.id, {
              ...body,
              // The PUT replaces every column, so the images have to travel with the edit or saving
              // a name change would drop them.
              logo_asset_id: d.dropLogo ? null : (template.logo_asset_id ?? null),
              banner_asset_id: d.dropBanner ? null : (template.banner_asset_id ?? null),
            })
          : template

      // Images last, and only now: a create has no row to attach them to until it exists.
      if (d.dropLogo && template?.logo_asset_id) await removeBrandingImage(row.id, 'logo')
      if (d.dropBanner && template?.banner_asset_id) await removeBrandingImage(row.id, 'banner')
      if (d.pendingLogo) await uploadBrandingImage(row.id, 'logo', d.pendingLogo)
      if (d.pendingBanner) await uploadBrandingImage(row.id, 'banner', d.pendingBanner)

      // ASSIGNMENT LAST, and STORED rather than deployed. Saving used to publish, which is why
      // Deploy had nothing to press: the audience lived in this draft and was handed straight to
      // the deploy endpoint. Now the branding carries it, so Deploy is a single action on the row
      // and this screen is purely authoring.
      //
      // Its own endpoint, not part of the PUT above: that one returns the branding to Building and
      // rebuilds it, and an audience is not among the bytes the signature covers — routing it
      // through there would unsign a branding every time somebody added a group.
      const assignmentsChanged =
        JSON.stringify(rowsToAssignments(d.assignments).assignments)
        !== JSON.stringify((template?.assignments as any) ?? [])
      if (assignmentsChanged) {
        const { assignments } = rowsToAssignments(d.assignments)
        await setBrandingTemplateAssignments(row.id, assignments)
      }
      return row
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['branding-templates'] })
      onSaved?.()
      onClose()
    },
    onError: (e: any) => {
      setError(errorText(e, 'Could not save the branding.'))
      // A NAME CLASH IS FIXED ON A STEP THE AUTHOR MAY NOT BE LOOKING AT. Saving happens from the
      // last step, and the name field is on the first — so the refusal would otherwise appear at
      // the bottom of the Assignment step, naming a field two screens away with no way to tell
      // which one. Go back to it and mark it, so the message points at something.
      if (e?.response?.status === 409) {
        setNameTaken(true)
        setStepIdx(STEPS.findIndex((s) => s.key === 'basics'))
      }
    },
  })

  const next = () => {
    if (step.key === 'basics' && !d.name.trim()) {
      setError('Give the branding a name.')
      return
    }
    setError(null)
    setStepIdx((i) => Math.min(i + 1, STEPS.length - 1))
  }

  const submit = () => {
    const blocked = problem(d, hasImage)
    if (blocked) {
      setError(blocked)
      return
    }
    setError(null)
    save.mutate()
  }

  return (
    <div className="w-full py-2">
      <h3 className="mb-4 text-lg font-semibold text-theme-text-primary">
        {template ? `Edit ${template.name}` : 'New branding'}
      </h3>

      <Stepper
        id={rail.id}
        steps={STEPS}
        current={stepIdx}
        onGoTo={(i) => setStepIdx(i)}
        ariaLabel="Branding steps"
      />

      <p className="mb-4 rounded-lg border border-theme-border-primary bg-theme-secondary px-4 py-2.5 text-xs text-theme-text-tertiary">
        A branding re-skins every application on the device — style, image, colour, company name and
        language. Behaviour (deferrals, countdowns, progress) always comes from the deployment&apos;s
        user-experience template.
      </p>

      {template && (
        <p className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2 text-xs text-amber-500">
          Saving returns this branding to Building: the signature covers the bytes that were built
          before. Devices already holding it keep what they have — a branding deployment carries its
          own frozen copy.
        </p>
      )}

      <div {...rail.panelProps}>
        {step.key === 'basics' && (
          <div className="mx-auto max-w-xl">
            <Field label="Name">
              <input
                className={`${inputCls}${nameTaken ? ' border-red-500' : ''}`}
                value={d.name}
                onChange={(e) => {
                  setNameTaken(false)
                  if (error) setError(null)
                  set('name', e.target.value)
                }}
                placeholder="Company standard"
                aria-invalid={nameTaken || undefined}
                aria-describedby={nameTaken ? 'branding-name-taken' : undefined}
                autoFocus
              />
              {nameTaken && (
                <p id="branding-name-taken" className="mt-1 text-xs text-red-400">
                  That name is already used by another branding. A device records the branding it
                  holds by name, so each one has to be different.
                </p>
              )}
            </Field>
            <Field label="Description" hint="Optional. Shown in the branding list.">
              <input
                className={inputCls}
                value={d.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="Who this branding is for"
              />
            </Field>
          </div>
        )}

        {step.key === 'appearance' && (
          <div className={previewGrid}>
            <div>
              <Field
                label="Dialog style"
                hint={
                  d.dialogStyle === 'Fluent'
                    ? 'The modern Fluent dialog.'
                    : d.dialogStyle === 'Classic'
                      ? 'The classic PSADT dialog with a banner. It ignores the accent colour.'
                      : 'Each deployment keeps its own style, so devices will show both.'
                }
              >
                <Select
                  value={d.dialogStyle}
                  onChange={(v) => {
                    const style = v as Draft['dialogStyle']
                    set('dialogStyle', style)
                    // Classic renders no accent, so pinning both would take a colour away from every
                    // deployment and show it nowhere.
                    if (style === 'Classic') set('accent', '')
                  }}
                  options={[
                    { value: '', label: 'Leave to the deployment' },
                    { value: 'Fluent', label: 'Fluent (modern)' },
                    { value: 'Classic', label: 'Classic' },
                  ]}
                />
                {/* The same warning the user-experience wizard raises, in the same place and words.
                    A branding pins the style for the WHOLE DEVICE, so the fallback it describes is
                    wider here, not narrower. */}
                {d.dialogStyle === 'Fluent' && (
                  <p className="mt-1.5 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2 text-xs text-amber-500">
                    Fluent needs .NET Framework 4.7.2 on the device. Windows 10 1803 and later ship
                    it; anything older falls back to the Classic dialog rather than failing.
                  </p>
                )}
                <DevNote
                  setting={`config.psd1 → UI.DialogStyle = ${
                    d.dialogStyle ? `'${d.dialogStyle}'` : '(not written)'
                  }`}
                  example="Written to %ProgramData%\\sap\\psadt\\config on every device this branding reaches."
                />
              </Field>

              {/* THE STYLE DECIDES WHICH ASSET APPLIES, so only that one is offered — an upload into
                  the slot the style does not draw is present, valid, signed and never rendered, and
                  nothing reports it. With no style pinned both are offered, because both will occur.
                  Clearing one never clears the other: the packager writes the logo into the banner
                  slot when no banner was supplied, so a single image brands both styles. */}
              {d.dialogStyle !== 'Classic' && (
                <>
                  <Field
                    label="Logo"
                    hint={
                      d.logoSource === 'appIcon'
                        ? "Uses each application's own packaged icon. Nothing to upload."
                        : 'A company image replaces the application icon in the Fluent dialog.'
                    }
                  >
                    <Select
                      value={d.logoSource}
                      onChange={(v) => set('logoSource', v as Draft['logoSource'])}
                      options={[
                        { value: 'appIcon', label: "App's own icon (automatic)" },
                        { value: 'custom', label: 'Custom logo' },
                      ]}
                    />
                    <DevNote
                      setting={
                        d.logoSource === 'appIcon'
                          ? 'Assets.Logo = (not written)'
                          : `Assets.Logo / Assets.LogoDark ← ${ASSET_SPEC.logo.file}`
                      }
                      example={
                        d.logoSource === 'appIcon'
                          ? "Each package keeps its own Assets\\AppIcon.png — the application's own icon."
                          : 'One upload fills both slots, so Fluent is branded in light and dark mode.'
                      }
                    />
                  </Field>
                  {d.logoSource === 'custom' && (
                    <Field label="Logo file" hint={ASSET_SPEC.logo.hint}>
                      <FileSlot
                        value={d.pendingLogo?.name ?? (savedLogo ? 'Current logo' : null)}
                        onPick={(f) => setD((p) => ({ ...p, pendingLogo: f, dropLogo: false }))}
                        onClear={() =>
                          setD((p) => ({ ...p, pendingLogo: null, dropLogo: !!template?.logo_asset_id }))
                        }
                      />
                      {logoLoadFailed && <SavedImageUnreadable slot="logo" />}
                    </Field>
                  )}
                </>
              )}

              {d.dialogStyle !== 'Classic' && (
                <Field
                  label="Accent colour"
                  hint="Colours the left bar, primary button and links in the Fluent dialog."
                >
                  <div className="flex flex-wrap items-center gap-2">
                    {ACCENT_PRESETS.map((hex) => (
                      <button
                        key={hex}
                        type="button"
                        onClick={() => set('accent', hex)}
                        title={hex}
                        aria-label={`Accent ${hex}`}
                        className={`h-7 w-7 rounded-full border-2 transition-transform ${
                          d.accent === hex
                            ? 'scale-110 border-theme-text-primary'
                            : 'border-transparent hover:scale-105'
                        }`}
                        style={{ background: hex }}
                      />
                    ))}
                    <label className="ml-1 flex items-center gap-1.5 text-xs text-theme-text-tertiary">
                      <input
                        type="color"
                        // Lowercase on purpose: <input type="color"> normalizes its value to
                        // lowercase in the DOM, so feeding it the uppercase stored form leaves React
                        // re-writing a value the browser keeps changing back.
                        value={(d.accent || DEFAULT_FLUENT_ACCENT).toLowerCase()}
                        onChange={(e) => set('accent', normalizeAccent(e.target.value))}
                        className="h-6 w-6 cursor-pointer rounded border border-theme-border-primary bg-transparent"
                      />
                      Custom
                    </label>
                    <button
                      type="button"
                      onClick={() => set('accent', '')}
                      className={`rounded-lg border px-2 py-1 text-xs ${
                        d.accent
                          ? 'border-theme-border-primary text-theme-text-tertiary hover:text-theme-text-primary'
                          : 'border-theme-text-primary text-theme-text-primary'
                      }`}
                    >
                      Leave to the deployment
                    </button>
                  </div>
                  <DevNote
                    setting={`config.psd1 → UI.FluentAccentColor = ${d.accent || '(not written)'}`}
                    example="'#RRGGBB'. The runtime converts it to the two's-complement Int32 PSADT binds to. Fluent only."
                  />
                </Field>
              )}

              {d.dialogStyle !== 'Fluent' && (
                <Field
                  label="Banner"
                  hint={`Across the top of the Classic dialog. ${ASSET_SPEC.banner.hint} Leave empty for the PSADT default.`}
                >
                  <FileSlot
                    value={d.pendingBanner?.name ?? (savedBanner ? 'Current banner' : null)}
                    onPick={(f) => setD((p) => ({ ...p, pendingBanner: f, dropBanner: false }))}
                    onClear={() =>
                      setD((p) => ({ ...p, pendingBanner: null, dropBanner: !!template?.banner_asset_id }))
                    }
                  />
                  {bannerLoadFailed && <SavedImageUnreadable slot="banner" />}
                  {/* SAID HERE OR DISCOVERED ON A DEVICE. A logo is shaped like an icon and a
                      banner is a 450x100 strip, so the logo cannot stand in for it — a branding
                      with no style pinned and no banner leaves every Classic deployment showing
                      PSADT's default. Only raised when that is the actual situation. */}
                  {!d.dialogStyle && hasLogoAsset && !hasBannerAsset && (
                    <p className="mt-1.5 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2 text-xs text-amber-500">
                      No style is pinned, so some deployments will render Classic — and those show
                      the default banner until you upload one. A logo cannot fill this slot: it is
                      the wrong shape, and the Fluent renderer rejects anything over 450×100.
                    </p>
                  )}
                  <DevNote
                    setting={`Assets.Banner ← ${ASSET_SPEC.banner.file}`}
                    example="Classic only. Written only when an image that fits 450×100 is available."
                  />
                </Field>
              )}

              <Field label="Company name" hint="Shown as the subtitle in every dialog on the device.">
                <input
                  className={inputCls}
                  value={d.companyName}
                  onChange={(e) => set('companyName', e.target.value)}
                  placeholder="Leave empty to follow each deployment"
                />
                <DevNote
                  setting={`config.psd1 → Toolkit.CompanyName = ${
                    d.companyName.trim() ? `'${d.companyName.trim()}'` : '(not written)'
                  }`}
                  example="Substituted into every {Toolkit\\CompanyName} subtitle as the string table loads."
                />
              </Field>

              <Field
                label="Dialog language"
                hint={
                  d.language
                    ? 'Every user on the device sees the dialogs in this language, whatever their ' +
                      'Windows display language.'
                    : "Each deployment keeps its own setting, which follows the user's Windows display language."
                }
              >
                <Select
                  value={d.language}
                  onChange={(v) => set('language', v)}
                  options={[
                    { value: '', label: 'Leave to the deployment' },
                    ...PSADT_LANGS.map((l) => ({ value: l.code, label: l.label })),
                  ]}
                />
                <DevNote
                  setting={`config.psd1 → UI.LanguageOverride = ${d.language ? `'${d.language}'` : '(not written)'}`}
                  example="PSADT ships the translated strings in every package; only which one loads is chosen here."
                />
              </Field>

              {/* Wording, not a language. It ships as a PSADT string table beside the
                  configuration, overriding the ONE string it names — so the other several hundred
                  keep the toolkit's own wording in whichever language the dialog loads. Placed
                  under the language picker because that is the question it is mistaken for. */}
              <Field
                label="Dialog wording"
                hint={
                  d.welcomeText.trim()
                    ? d.language
                      ? 'Shown as written, to every user. The rest of the dialog stays in the ' +
                        'language above — this line does not follow it.'
                      : 'Shown as written, to every user, whatever language the rest of the ' +
                        'dialog is in. Only this line is replaced.'
                    : "Optional. Left empty, users see PSADT's own wording in their own language."
                }
              >
                <textarea
                  className={`${inputCls} min-h-[72px] resize-y`}
                  value={d.welcomeText}
                  onChange={(e) => set('welcomeText', e.target.value)}
                  placeholder="Acme IT is updating this application. Please save your work."
                />
                <DevNote
                  setting={`Strings\\strings.psd1 → CloseAppsPrompt.CustomMessage${
                    d.welcomeText.trim() ? '' : ' (no string table shipped)'
                  }`}
                  example="Super-imposed over the toolkit's table, so every string not named here keeps PSADT's translation."
                />
              </Field>
            </div>

            <div>
              <p className="mb-2 text-xs font-medium text-theme-text-secondary">Live preview</p>
              <PreviewPane
                d={d}
                logoUrl={logoUrl}
                bannerUrl={bannerUrl}
                hasLogo={hasLogo}
              />
              <p className="mt-2 text-[11px] text-theme-text-tertiary">
                The application, and whether the dialog offers a deferral, come from each
                deployment — they are shown here only so the dialog is complete. Only the image,
                company name, style and accent are this branding&apos;s.
              </p>
            </div>
          </div>
        )}

        {step.key === 'assignment' && (
          <div className="mx-auto max-w-3xl">
            {/* REQUIRED AND UNINSTALL ONLY. A branding is a re-skin of every dialog on the machine
                — nobody browses to one in Company Portal — so `available` would put it in a
                catalogue where it makes no sense. Uninstall is offered because taking a branding
                back off a device is a real action: it restores the packaged default. */}
            {assignmentNote(template) && (
              <p className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-4 py-2.5 text-xs text-amber-500">
                {assignmentNote(template)}
              </p>
            )}
            {groupsQ.isError && (
              <p className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-4 py-2.5 text-xs text-amber-500">
                The tenant&apos;s Entra security groups could not be read, so only All users and
                All devices can be targeted here. Check the Intune connection under Settings.
              </p>
            )}
            <AssignmentBlade
              value={d.assignments}
              onChange={(rows) => set('assignments', rows)}
              intents={['required', 'uninstall']}
              groups={groups}
              filters={filtersQ.data?.filters ?? []}
              // No pattern to name a group after and no publish step to create one: a branding
              // deployment assigns to groups that already exist.
              allowGeneratedGroups={false}
              note={
                <p className="rounded-lg border border-theme-border-primary bg-theme-secondary px-4 py-2.5 text-xs text-theme-text-tertiary">
                  Normally assigned as{' '}
                  <span className="font-medium text-theme-text-secondary">Required</span>.
                  Uninstall removes the branding from devices that already have it, restoring the
                  packaged default. Saving records who receives it; <span
                  className="font-medium text-theme-text-secondary">Deploy</span> on the branding
                  list is what publishes it to Intune.
                </p>
              }
            />
          </div>
        )}
      </div>

      {error && <p className="mt-3 text-xs text-red-400">{error}</p>}

      <div className="mt-6 flex items-center justify-end gap-2 border-t border-theme-border-primary pt-4">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-theme-border-primary px-3 py-1.5 text-sm text-theme-text-secondary"
        >
          Cancel
        </button>
        {stepIdx > 0 && (
          <button
            type="button"
            onClick={() => setStepIdx((i) => i - 1)}
            className="rounded-lg border border-theme-border-primary px-3 py-1.5 text-sm text-theme-text-secondary"
          >
            Back
          </button>
        )}
        {isLast ? (
          <button
            type="button"
            onClick={submit}
            disabled={save.isPending}
            className="rounded-lg bg-theme-accent-blue px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : template ? 'Save branding' : 'Create branding'}
          </button>
        ) : (
          <button
            type="button"
            onClick={next}
            className="rounded-lg bg-theme-accent-blue px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            Continue
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * The dialog this branding produces, drawn by the SAME renderer the user-experience wizard uses so
 * the two screens cannot disagree about what an image, an accent or a style will look like.
 *
 * A branding that pins no style is previewable both ways rather than guessed at — it will be seen
 * both ways on the estate. Behaviour values here are stand-ins: branding does not set them.
 */
const PreviewPane: React.FC<{
  d: Draft
  logoUrl: string | null
  bannerUrl: string | null
  hasLogo: boolean
}> = ({ d, logoUrl, bannerUrl, hasLogo }) => {
  const pinned = d.dialogStyle === 'Fluent' ? 'fluent' : d.dialogStyle === 'Classic' ? 'classic' : null
  const [shown, setShown] = useState<'fluent' | 'classic'>(pinned ?? 'fluent')
  const style = pinned ?? shown

  const cfg: PsadtPreviewConfig = {
    style,
    accent: d.accent || DEFAULT_FLUENT_ACCENT,
    companyName: d.companyName,
    logoSource: d.logoSource,
    hasLogo,
    // THE BANNER IS THE BANNER, and no longer falls back to the logo. The package reuses a logo in
    // that slot only when it fits 450x100, which a logo shaped like an icon does not — so drawing
    // one here would promise Classic branding the device will not show.
    hasBanner: !!bannerUrl,
    logoUrl: logoUrl ?? undefined,
    bannerUrl: bannerUrl ?? undefined,
    allowDefer: true,
    deferCount: 3,
    showCountdown: false,
    countdownSeconds: 0,
    showProgress: true,
    deploymentType: 'install',
    visible: true,
    mode: 'notify',
    timeoutMinutes: 55,
  }

  return (
    <div>
      {!pinned && (
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="text-[11px] text-theme-text-tertiary">
            No style pinned — deployments choose, so both occur.
          </span>
          <div className="flex overflow-hidden rounded-md border border-theme-border-primary">
            {(['fluent', 'classic'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setShown(s)}
                className={`px-2 py-1 text-[11px] font-medium ${
                  shown === s
                    ? 'bg-theme-tertiary text-theme-text-primary'
                    : 'text-theme-text-tertiary hover:text-theme-text-secondary'
                }`}
              >
                {s === 'fluent' ? 'Fluent' : 'Classic'}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* BOUNDED, like the user-experience wizard's. The dialog is a fixed-size window on the
          device; stretched to a full-width column it stops previewing anything, because the
          proportions are what tell an author how their logo will actually sit. */}
      <div className="mx-auto w-full" style={{ maxWidth: PSADT_PREVIEW_MAX_WIDTH }}>
        <PsadtDialogSurface cfg={cfg} />
      </div>
    </div>
  )
}

export default BrandingWizard
